// src/app/core/services/app-update.service.ts
import { Injectable, OnDestroy, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Subject, filter, interval, takeUntil } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
    private readonly swUpdate = inject(SwUpdate);
    private readonly destroy$ = new Subject<void>();

    readonly updateAvailable$ = new BehaviorSubject<boolean>(false);

    private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000;

    constructor() {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.listenForUpdates();
        this.schedulePeriodicChecks();
        this.checkNow();
    }

    private listenForUpdates(): void {
        this.swUpdate.versionUpdates
            .pipe(
                filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.updateAvailable$.next(true);
            });
    }

    private schedulePeriodicChecks(): void {
        interval(this.CHECK_INTERVAL_MS)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.checkNow());
    }

    private async checkNow(): Promise<void> {
        try {
            await this.swUpdate.checkForUpdate();
        } catch {
            // silent
        }
    }

    async applyUpdate(): Promise<void> {
        try {
            await this.swUpdate.activateUpdate();
        } catch {
            // silent
        } finally {
            window.location.reload();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
